import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import T from '../theme';
import { WhyThisMatters } from '../components/Shared.jsx';

export function SponsorFinder({ data }) {
  const { sponsors = [], possible_sponsors = [], summary, notes = [] } = data;

  return (
    <Box>
      {summary && (
        <Box sx={{ mb: 3, p: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1 }}>
          <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: T.textSecondary }}>{summary}</Typography>
        </Box>
      )}

      {sponsors.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.success, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Likely Sponsors</Typography>
          {sponsors.map((s, i) => (
            <SponsorCard key={i} sponsor={s} variant="confirmed" />
          ))}
        </Box>
      )}

      {possible_sponsors.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.warning, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Possible Sponsors</Typography>
          {possible_sponsors.map((s, i) => (
            <SponsorCard key={i} sponsor={s} variant="possible" />
          ))}
        </Box>
      )}

      {notes.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {notes.map((n, i) => <Typography key={i} sx={{ fontSize: 13, color: T.textSecondary, mb: 0.5 }}>• {n}</Typography>)}
        </Box>
      )}

      <WhyThisMatters text="Knowing who sponsors a show helps you benchmark rates and identify competitive gaps." citations={data.expertCitations} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sponsor card with expandable evidence
// ---------------------------------------------------------------------------
function SponsorCard({ sponsor: s, variant }) {
  const [expanded, setExpanded] = useState(false);
  const evidenceList = s.evidence || [];
  const episodeCount = evidenceList.filter((e) => e.episode_title).length;
  const isConfirmed = variant === 'confirmed';
  const bgBase = isConfirmed ? 'rgba(34,197,94,' : 'rgba(234,179,8,';

  return (
    <Box sx={{ p: 1.75, mb: 1.25, background: `${bgBase}0.06)`, border: `1px solid ${bgBase}0.25)`, borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: T.text }}>{s.brand}</Typography>
        {s.sponsor_type && <Box sx={{ px: 1, py: 0.25, background: 'rgba(249,115,22,0.2)', color: T.primary, borderRadius: 4, fontSize: 11 }}>{s.sponsor_type}</Box>}
        {s.confidence && <Box sx={{ px: 1, py: 0.25, background: s.confidence === 'high' ? 'rgba(34,197,94,0.15)' : 'rgba(234,179,8,0.15)', color: s.confidence === 'high' ? T.success : T.warning, borderRadius: 4, fontSize: 11 }}>{s.confidence}</Box>}
      </Box>

      {/* Date range + episode count */}
      <Typography sx={{ fontSize: 11, color: T.textSecondary }}>
        {s.first_seen && <>First seen: {s.first_seen}</>}
        {s.last_seen && <> · Last seen: {s.last_seen}</>}
        {episodeCount > 0 && <> · <Box component="span" sx={{ color: T.text, fontWeight: 600 }}>{episodeCount} episode{episodeCount !== 1 ? 's' : ''}</Box></>}
      </Typography>

      {/* Expandable evidence */}
      {evidenceList.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Box
            component="button"
            onClick={() => setExpanded(!expanded)}
            sx={{
              background: 'none', border: 'none', color: T.accent, fontSize: 11,
              cursor: 'pointer', p: 0, textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {expanded ? '▾ Hide evidence' : `▸ Show evidence (${evidenceList.length})`}
          </Box>
          {expanded && (
            <Box sx={{ mt: 0.75, pl: 1, borderLeft: `2px solid ${T.border}` }}>
              {evidenceList.map((ev, j) => (
                <Box key={j} sx={{ mb: 0.75 }}>
                  {ev.episode_title && (
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.text }}>{ev.episode_title}</Typography>
                  )}
                  {ev.snippet && (
                    <Typography sx={{ fontSize: 11, color: T.textSecondary, fontStyle: 'italic' }}>"{ev.snippet}"</Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {ev.source && <Typography sx={{ fontSize: 10, color: T.textSecondary }}>{ev.source}</Typography>}
                    {ev.url && ev.url !== 'unknown' && (
                      <Typography component="a" href={ev.url} target="_blank" rel="noreferrer" sx={{ fontSize: 10, color: T.accent, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>link</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Reasoning for possible sponsors */}
      {!isConfirmed && s.reasoning && (
        <Typography sx={{ fontSize: 12, color: T.textSecondary, mt: 0.5 }}>{s.reasoning}</Typography>
      )}
    </Box>
  );
}
